import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import { Link } from 'react-router-dom';
import { Res } from '@cbo/shared-library';
import { isNil } from 'lodash';
import DeltaChipIndicator from '../DeltaIndicatorChip/DeltaIndicatorChip';
import { MobileSize } from '../../admin/DashboardPage/ScorecardContainer/ScorecardContainer';
import { formatCurrency } from '../../utils/formatters';
import ComparisonRange from '../../config/comparisonRange';
import { DateComparisonRanges } from '../GlobalFilterBar/types';
import { rowFlex } from '../../lib/styles/commonStyles';
import colors from '../../lib/styles/colors';

export interface SingleValueCardProps {
  title: string;
  value: string | number;
  loading: boolean;
  comparisonValue?: string | number | null;
  hideDeltaOnlyWhenNull?: boolean;
  comparisonRange?: ComparisonRange | DateComparisonRanges | null;
  comparisonDateRange?: {
    startDate: string | null;
    endDate: string | null;
  } | null;
  positiveResultsPreferred?: boolean;
  currency?: boolean;
  percentage?: boolean;
  url?: string;
  mobileSize?: MobileSize;
  width?: string;
  preferences?: Res.Admin.UserPreferences | null;
  testId?: string;
  height?: string;
  isClickable?: boolean;
  handleClick?: () => void;
  chipLabel?: string;
  chipColor?: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  chipIcon?: React.ReactElement;
  comparisonText?: string;
  icon?: React.ReactNode;
  hideSign?: boolean;
}

// Helper function to format the value based on type
const formatValue = (
  value: string | number,
  currency: boolean | undefined,
  percentage: boolean | undefined,
  preferences: Res.Admin.UserPreferences | null | undefined
): string | number => {
  if (currency) {
    const valueNumber = Number(value);
    const absValue = Math.abs(valueNumber);
    const formatted = formatCurrency(absValue, preferences);
    return valueNumber < 0 ? `(${formatted})` : formatted;
  }

  if (percentage) {
    return `${Number(value).toFixed(2)}%`;
  }

  return value;
};

// Helper function to determine if comparison value should be shown
const shouldShowComparisonValue = (
  comparisonValue: string | number | null | undefined,
  hideDeltaOnlyWhenNull: boolean | undefined
): boolean => {
  if (hideDeltaOnlyWhenNull) {
    return !isNil(comparisonValue);
  }
  return comparisonValue !== 0 && !isNil(comparisonValue);
};

// Helper function to calculate common styles
const getCommonStyles = (
  mobileSize: MobileSize | undefined,
  width: string | undefined,
  height: string | undefined
) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: mobileSize === 'small' ? 'column' : 'row',
  justifyContent: 'center',
  alignItems: 'center',
  height: height || '142px',
  width: mobileSize === 'small' ? width || '100%' : '185px',
  overflow: 'clip',
  flex: mobileSize && mobileSize !== 'small' ? '40%' : 'none',
  marginBottom: mobileSize === 'small' ? '-8px' : 0,
  border: 1,
  borderColor: colors.borderOnEmpty,
  borderRadius: '8px',
  boxShadow: 'none',
});

const clickableStyles = {
  '&:hover': {
    boxShadow:
      '0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
  },
};

export default function SingleValueCard({
  title,
  value,
  loading,
  comparisonRange,
  comparisonDateRange,
  comparisonValue,
  hideDeltaOnlyWhenNull,
  positiveResultsPreferred,
  currency,
  percentage,
  url,
  mobileSize,
  width,
  height,
  preferences,
  testId,
  isClickable,
  handleClick,
  chipLabel,
  chipColor,
  chipIcon,
  comparisonText,
  icon,
  hideSign,
}: SingleValueCardProps) {
  const formattedValue = formatValue(value, currency, percentage, preferences);
  const isComparisonValuePresent = shouldShowComparisonValue(comparisonValue, hideDeltaOnlyWhenNull);
  const commonSx = getCommonStyles(mobileSize, width, height);
  const paperSx = isClickable ? { ...commonSx, ...clickableStyles } : commonSx;
  const testIdValue = testId ? `single-value-card-small-${testId}` : 'single-value-card-small';

  const handleCardClick = () => {
    if (isClickable && handleClick) {
      handleClick();
    }
  };

  return (
    <Paper sx={paperSx} data-testid={testIdValue}>
      {loading ? (
        <Skeleton
          sx={{
            height: '100%',
            width: '100%',
          }}
          variant='rounded'
          data-testid='single-value-card-small-skeleton'
        />
      ) : (
        <Box
          hidden={loading}
          sx={{
            position: 'relative',
            justifyContent: 'center',
            display: 'flex',
            width: '100%',
            height: '100%',
          }}
          onClick={handleCardClick}
          data-testid='single-value-card-small-content'
        >
          {isClickable && url && (
            <Link
              to={url}
              title={url}
              data-testid='single-value-card-url'
              style={{ width: '100%', height: '100%', position: 'absolute' }}
            />
          )}
          <Box
            data-testid='single-value-card-small-content'
            sx={{
              display: 'flex',
              flexDirection: 'column',
              verticalAlign: 'middle',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: icon ? 2 : 0,
                marginTop: '4px',
              }}
            >
              {icon}
              <Typography
                sx={{
                  fontSize: '24px',
                  fontWeight: '500',
                  letterSpacing: '0.15px',
                }}
                data-testid='single-value-card-small-value'
              >
                {formattedValue}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: '400',
                lineHeight: '1em',
                letterSpacing: '0.15px',
                marginTop: '4px',
                textAlign: 'center',
                whiteSpace: 'pre-line',
              }}
              data-testid='single-value-card-small-title'
            >
              {title}
            </Typography>
            {(comparisonRange || comparisonDateRange) && (
              <DeltaChipIndicator
                comparisonRange={isComparisonValuePresent ? comparisonRange : undefined}
                comparisonDateRange={comparisonDateRange}
                value={comparisonValue ?? undefined}
                isImprovement={positiveResultsPreferred ? Number(comparisonValue) > 0 : Number(comparisonValue) < 0}
                dataTestId='single-value-card-small-delta'
                hideSign={hideSign}
              />
            )}
            {chipLabel && (
              <Box
                sx={{
                  ...rowFlex,
                  alignItems: 'center',
                  marginTop: '8px',
                  gap: 1,
                }}
              >
                <Chip
                  label={chipLabel}
                  color={chipColor}
                  size='small'
                  icon={chipIcon}
                  data-testid='single-value-card-chip'
                />
                {comparisonText && (
                  <Typography variant='body2' color='text.secondary' data-testid='single-value-card-comparison-text'>
                    {comparisonText}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Paper>
  );
}

SingleValueCard.defaultProps = {
  comparisonRange: undefined,
  comparisonDateRange: undefined,
  comparisonValue: undefined,
  hideDeltaOnlyWhenNull: false,
  currency: false,
  percentage: false,
  mobileSize: undefined,
  positiveResultsPreferred: true,
  url: '',
  testId: undefined,
  width: undefined,
  height: undefined,
  preferences: null,
  isClickable: false,
  handleClick: undefined,
  chipLabel: undefined,
  chipColor: 'default',
  chipIcon: undefined,
  comparisonText: undefined,
  icon: undefined,
  hideSign: false,
};
